import React, { useCallback, useEffect, useState } from 'react'
import { Col, Form, Modal, Row } from 'react-bootstrap'
import Button from '@components/common/Button/Button.component'
import InputField from '@components/common/InputField/InputField.component'
import { TIME_OPTIONS } from '@constants/Providers'
import './AddOverrideModal.css'

export type ScheduleOverrideFormValues = {
  name: string
  date: string
  start_time: string
  end_time: string
}

const emptyForm = (): ScheduleOverrideFormValues => ({
  name: '',
  date: '',
  start_time: '',
  end_time: '',
})

type AddOverrideModalProps = {
  show: boolean
  onHide: () => void
  onSubmit: (values: ScheduleOverrideFormValues) => void
  initialValues?: ScheduleOverrideFormValues | null
  mode: 'add' | 'edit'
}

const AddOverrideModal: React.FC<AddOverrideModalProps> = ({
  show,
  onHide,
  onSubmit,
  initialValues,
  mode,
}) => {
  const [form, setForm] = useState<ScheduleOverrideFormValues>(emptyForm)

  useEffect(() => {
    if (!show) return
    setForm(initialValues ? { ...initialValues } : emptyForm())
  }, [show, initialValues])

  const handleSubmit = useCallback((): void => {
    if (!form.date) return
    if (!form.start_time.trim() || !form.end_time.trim()) return
    onSubmit({
      name: form.name.trim(),
      date: form.date,
      start_time: form.start_time,
      end_time: form.end_time,
    })
  }, [form, onSubmit])

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      className="custom-modal-width add-override-modal"
      backdrop="static"
    >
      <Modal.Header className="bg-custom3 mb-1" closeButton>
        <Modal.Title>
          {mode === 'edit' ? 'Edit override' : 'Add override'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form
          id="add-override-form"
          onSubmit={(e): void => {
            e.preventDefault()
            handleSubmit()
          }}
        >
          <Row className="gx-2 mb-2">
            <Col md={12}>
              <InputField
                label="Override name"
                placeholder="e.g. Business trip"
                value={form.name}
                onChange={(e) =>
                  setForm((p) => ({ ...p, name: e.target.value }))
                }
              />
            </Col>
          </Row>
          <Row className="gx-2 mb-2">
            <Col md={12}>
              <Form.Group controlId="overrideDate">
                <Form.Label>Select date</Form.Label>
                <Form.Control
                  type="date"
                  className="overrideDate"
                  value={form.date}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, date: e.target.value }))
                  }
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="gx-2 mb-2">
            <Col md={6}>
              <Form.Group controlId="overrideShiftStart">
                <Form.Label>Shift start</Form.Label>
                <Form.Select
                  value={form.start_time}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, start_time: e.target.value }))
                  }
                >
                  <option value="">Select</option>
                  {TIME_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="overrideShiftEnd">
                <Form.Label>Shift end</Form.Label>
                <Form.Select
                  value={form.end_time}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, end_time: e.target.value }))
                  }
                >
                  <option value="">Select</option>
                  {TIME_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button type="button" className="button-secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="add-override-form"
          className="button-primary"
        >
          {mode === 'edit' ? 'Save changes' : 'Add'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default AddOverrideModal
